// @flow
import React from "react";
import { noop } from "lodash";
import tvgConf from "@tvg/conf";
import {
  Checkbox,
  Header,
  Paragraph,
  Switch,
  Loading
} from "@tvg/design-system";
import type { ToggleGroupElement } from "./types";
import buildColor from "../../_static/ColorPalette";
import Icon from "../../_static/Icons";
import { chevronRight } from "../../_static/Icons/iconsDuotone";
import {
  ButtonContainer,
  PrefToggleContainer,
  TitleDescriptionContainer,
  ToggleContainer,
  SwitchWrapper
} from "./styled-components";

type Props = {
  title: string,
  description?: string,
  toggles: Array<ToggleGroupElement>,
  qaLabel: string
};

const PreferencesToggleGroup = ({
  title,
  description,
  toggles,
  qaLabel
}: Props) => {
  const isMobile = tvgConf().device === "mobile";
  const isTablet = tvgConf().device === "tablet";

  const renderTogglesMobile = () =>
    toggles.map((toggle: ToggleGroupElement) =>
      toggle.btnType ? (
        <ButtonContainer
          to={toggle.url}
          onClick={toggle.onClick}
          $isTablet={isTablet}
          data-qa-label={`${toggle.qaLabel}-button`}
        >
          <Paragraph
            qaLabel={`${toggle.qaLabel}-text`}
            fontSize={14}
            fontFamily="regular"
            color={buildColor("grey", "900")}
          >
            {toggle.name}
          </Paragraph>
          <Icon
            icon={chevronRight}
            size={16}
            viewBoxSize={16}
            color="transparent"
            strokeColorList={[buildColor("grey", "800")]}
          />
        </ButtonContainer>
      ) : (
        <ToggleContainer key={toggle.name} $isTablet={isTablet}>
          <Paragraph qaLabel={`${toggle.qaLabel}-toggle-name`}>
            {toggle.name}
          </Paragraph>
          <SwitchWrapper>
            {toggle.isLoading && (
              <Loading
                size="s"
                color="blue_accent.500"
                bgColor="blue_accent.200"
                qaLabel="switch-spinner"
              />
            )}
            <Switch
              isOn={toggle.defaultValue}
              onPress={toggle.onClick}
              qaLabel={`${toggle.qaLabel}-toggle-${(toggle.defaultValue: any)}`}
              isDisabled={toggle.isDisabled || false}
            />
          </SwitchWrapper>
        </ToggleContainer>
      )
    );

  const renderTogglesDesktop = () =>
    toggles.map((toggle: ToggleGroupElement) => (
      <ToggleContainer key={toggle.name} isDesktop>
        <Checkbox.Group
          isDisabled={toggle.isLoading || toggle.isDisabled || false}
          selectedValues={toggle.defaultValue ? [toggle.qaLabel] : []}
          onChange={toggle.onClick}
          qaLabel={`${toggle.qaLabel}-${(toggle.defaultValue: any)}-group`}
        >
          <Checkbox
            value={toggle.qaLabel}
            label={toggle.name}
            qaLabel={`${toggle.qaLabel}-${(toggle.defaultValue: any)}-checkbox`}
          />
        </Checkbox.Group>
      </ToggleContainer>
    ));

  return (
    <PrefToggleContainer data-qa-label={qaLabel}>
      <TitleDescriptionContainer>
        <Header tag="h3" qaLabel={`${qaLabel}-title`}>
          {title}
        </Header>
        {description && (
          <Paragraph qaLabel={`${qaLabel}-description`} color="grey.700">
            {description}
          </Paragraph>
        )}
      </TitleDescriptionContainer>
      {isMobile || isTablet ? renderTogglesMobile() : renderTogglesDesktop()}
    </PrefToggleContainer>
  );
};

PreferencesToggleGroup.defaultProps = {
  title: "",
  description: "",
  toggles: [
    {
      name: "",
      onClick: noop,
      defaultValue: false,
      qaLabel: "",
      btnType: false
    }
  ]
};

export default PreferencesToggleGroup;
